/* ============================================================================
   BLOOD ENGINE v1.2 — splashtank / Halloween 2026
   ----------------------------------------------------------------------------
   A real droplet simulation, not a CSS loop.

   Each bead of blood is a body with mass, and the ledge holds it by surface
   tension. Mass grows as blood feeds in; when weight beats tension the bead
   necks, snaps, and falls under gravity, leaving a streak on the "glass"
   that slowly runs and dries.

   The ledge is pinned to the underside of the sticky header, measured live,
   so the blood seeps out from beneath the site chrome instead of painting
   over the announcement bar. That one decision is the difference between
   "this store is bleeding" and "this store has a rendering bug".

   Signature behaviours:
     - BLEED-ON-SCROLL  the deeper the visitor scrolls, the harder the store
                        bleeds. The landing view is already wet; the footer is
                        drowning. Wired to scroll DEPTH, not scroll events.
     - SMEAR            fast scrolling drags the falling blood, so motion
                        bends the fluid the way a camera whip-pan would.
     - REPEL            the cursor pushes beads aside; they wobble back.
     - HEARTBEAT        one global pulse drives feed rate, so drips breathe
                        together instead of animating independently.

   Everything else is read from data-* attributes written by the theme editor,
   so the merchant tunes the simulation without touching this file.
   Renders to a single canvas. Never intercepts a click. Sleeps when hidden.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.getElementById('BloodEngine');
  if (!root) return;

  // Respect the visitor before respecting the art direction.
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // ---- Settings, straight from the theme editor ---------------------------
  var d = root.dataset;
  function num(key, fallback) {
    var v = parseFloat(d[key]);
    return isNaN(v) ? fallback : v;
  }

  var CFG = {
    beads:       Math.round(num('beads', 16)),
    speed:       num('speed', 1),
    thickness:   num('thickness', 1),
    gravity:     num('gravity', 1),
    bleed:       num('bleed', 1),
    smear:       num('smear', 1),
    interactive: d.interactive !== 'false',
    color:       d.color || '#9c0d13',
    highlight:   d.highlight || '#d8262c',
    shadow:      d.shadow || '#4a0308'
  };

  if (CFG.beads <= 0) return;

  // ---- Canvas -------------------------------------------------------------
  var canvas = document.createElement('canvas');
  canvas.className = 'hw-blood__canvas';
  canvas.setAttribute('aria-hidden', 'true');
  root.appendChild(canvas);
  var ctx = canvas.getContext('2d');

  var W = 0, H = 0, DPR = 1;
  var ledgeTop = 0, ledgeH = 0, beadY = 0;

  // The ledge hangs off whatever chrome is pinned to the top of the window.
  var headerEl = document.querySelector('.site-header');
  function measureLedgeTop() {
    ledgeTop = headerEl ? Math.max(0, Math.round(headerEl.getBoundingClientRect().bottom)) : 0;
  }

  function resize() {
    DPR = Math.min(window.devicePixelRatio || 1, 2);
    W = window.innerWidth;
    H = window.innerHeight;
    canvas.width = Math.round(W * DPR);
    canvas.height = Math.round(H * DPR);
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    measureLedgeTop();
    layout();
  }

  // ---- Deterministic pseudo-random ---------------------------------------
  // Seeded, so the drip pattern is stable across reloads: the store looks
  // like itself every visit instead of reshuffling into a new design.
  var seed = 20261031;
  function rnd() {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  }

  // ---- Model --------------------------------------------------------------
  var beads = [], drops = [], streaks = [];

  function layout() {
    seed = 20261031;
    beads.length = 0;
    var n = CFG.beads;
    for (var i = 0; i < n; i++) {
      // Even spacing plus jitter: regular enough to read as a deliberate
      // ledge, irregular enough that the eye never finds the pattern.
      var slot = (i + 0.5) / n;
      var jitter = (rnd() - 0.5) * (0.85 / n);
      var b = {
        x: (slot + jitter) * W,
        r: (6 + rnd() * 8) * CFG.thickness,
        rBase: 0,
        // Start part-grown, so the landing view already has hanging beads
        // instead of a bare ledge that takes ten seconds to become the design.
        mass: 0.35 + rnd() * 0.45,
        feed: 0.10 + rnd() * 0.22,
        phase: rnd() * Math.PI * 2,
        vx: 0,
        neck: 0
      };
      b.rBase = b.r;
      beads.push(b);
    }
  }

  // ---- Input --------------------------------------------------------------
  var mouse = { x: -9999, y: -9999, active: false };
  if (CFG.interactive) {
    window.addEventListener('mousemove', function (e) {
      mouse.x = e.clientX; mouse.y = e.clientY; mouse.active = true;
    }, { passive: true });
    window.addEventListener('mouseleave', function () { mouse.active = false; });
  }

  // Scroll depth drives the bleed; scroll velocity drives the smear.
  var depth = 0, velocity = 0, lastScroll = window.scrollY || 0;
  window.addEventListener('scroll', function () {
    var y = window.scrollY || 0;
    var max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    depth = Math.min(1, y / max);
    velocity = velocity * 0.6 + Math.abs(y - lastScroll) * 0.4;
    lastScroll = y;
    measureLedgeTop();   // the announcement bar scrolls away; the header stays
  }, { passive: true });

  // ---- Paint --------------------------------------------------------------
  function bloodGradient(r) {
    var g = ctx.createLinearGradient(0, -r, 0, r * 1.6);
    g.addColorStop(0, CFG.shadow);
    g.addColorStop(0.45, CFG.color);
    g.addColorStop(1, CFG.shadow);
    return g;
  }

  // A hanging bead: a circle pulled into a teardrop by its own weight.
  function drawBead(b) {
    var stretch = 1 + b.neck * 1.5;
    var r = b.r;

    ctx.save();
    ctx.translate(b.x + b.vx, beadY);
    ctx.scale(1, stretch);

    ctx.beginPath();
    // The neck narrows as the bead gets ready to let go.
    var neckW = r * (1 - b.neck * 0.72);
    ctx.moveTo(-neckW, 0);
    ctx.bezierCurveTo(-r * 1.05, r * 0.55, -r * 0.95, r * 1.5, 0, r * 1.55);
    ctx.bezierCurveTo(r * 0.95, r * 1.5, r * 1.05, r * 0.55, neckW, 0);
    ctx.closePath();
    ctx.fillStyle = bloodGradient(r);
    ctx.fill();

    // Specular dot: the single detail that makes it read as wet.
    ctx.beginPath();
    ctx.ellipse(-r * 0.3, r * 0.55, r * 0.2, r * 0.34, -0.4, 0, Math.PI * 2);
    ctx.fillStyle = CFG.highlight;
    ctx.globalAlpha = 0.5;
    ctx.fill();
    ctx.restore();
  }

  function drawDrop(p) {
    // Falling blood elongates with speed: the faster it goes, the thinner
    // and longer it reads, the way real liquid does under a shutter.
    var stretch = Math.min(4.5, 1 + p.vy * 0.16);
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.scale(1, stretch);
    ctx.beginPath();
    ctx.arc(0, 0, p.r, 0, Math.PI * 2);
    ctx.fillStyle = bloodGradient(p.r);
    ctx.fill();
    ctx.restore();
  }

  function drawLedge() {
    var top = ledgeTop, h = ledgeH, lip = top + h * 0.72;
    var g = ctx.createLinearGradient(0, top, 0, top + h);
    g.addColorStop(0, CFG.shadow);
    g.addColorStop(0.5, CFG.color);
    g.addColorStop(1, CFG.shadow);
    ctx.fillStyle = g;

    // The underside sags between beads, held up by surface tension.
    ctx.beginPath();
    ctx.moveTo(0, top);
    ctx.lineTo(W, top);
    ctx.lineTo(W, lip);
    for (var i = beads.length - 1; i >= 0; i--) {
      var b = beads[i];
      var sag = lip + b.r * 0.5;
      ctx.quadraticCurveTo(b.x + b.r, sag, b.x, sag);
      ctx.quadraticCurveTo(b.x - b.r, sag, b.x - b.r * 2.2, lip);
    }
    ctx.lineTo(0, lip);
    ctx.closePath();
    ctx.fill();

    ctx.globalAlpha = 0.35;
    ctx.fillStyle = CFG.highlight;
    ctx.fillRect(0, top + h * 0.18, W, h * 0.14);
    ctx.globalAlpha = 1;
  }

  // ---- Simulation ---------------------------------------------------------
  var last = performance.now();
  var beat = 0;
  var running = true;

  document.addEventListener('visibilitychange', function () {
    running = !document.hidden;
    if (running) { last = performance.now(); requestAnimationFrame(tick); }
  });

  function tick(now) {
    if (!running) return;
    // Capped so a tab-switch does not teleport the whole simulation.
    var dt = Math.min(50, now - last) / 16.666;
    last = now;
    dt *= CFG.speed;

    beat += dt * 0.035;
    velocity *= 0.9;

    // BLEED-ON-SCROLL. The base term is what a visitor meets on arrival, so
    // it has to already read as blood; the depth term is the escalation.
    var bleed = 1 + depth * 2.6 * CFG.bleed;
    ledgeH = (20 + depth * 30 * CFG.bleed) * CFG.thickness;
    beadY = ledgeTop + ledgeH * 0.72;

    ctx.clearRect(0, 0, W, H);

    // Streaks first: they live behind everything else.
    for (var s = streaks.length - 1; s >= 0; s--) {
      var st = streaks[s];
      st.life -= dt * 0.004 / Math.max(0.2, CFG.smear);
      st.y += st.vy * dt;
      st.vy *= 0.985;
      if (st.life <= 0) { streaks.splice(s, 1); continue; }
      ctx.globalAlpha = st.life * 0.4;
      ctx.fillStyle = CFG.shadow;
      ctx.fillRect(st.x - st.w / 2, st.top, st.w, st.y - st.top);
      ctx.globalAlpha = 1;
    }

    // Beads on the ledge.
    for (var i = 0; i < beads.length; i++) {
      var b = beads[i];

      // One shared heartbeat, so the ledge breathes as a single body.
      var pulse = 0.65 + 0.35 * Math.sin(beat + b.phase);
      b.mass += dt * 0.004 * b.feed * pulse * bleed;
      b.r = b.rBase * (1 + b.mass * 0.85);

      if (mouse.active) {
        var dx = b.x + b.vx - mouse.x;
        var dy = beadY - mouse.y;
        var dist2 = dx * dx + dy * dy;
        if (dist2 < 16000) {
          b.vx += (dx / (Math.sqrt(dist2) + 1)) * 1.4 * dt;
        }
      }
      b.vx *= 0.9;
      b.vx += (0 - b.vx) * 0.06 * dt;

      // Necking: past the tension limit the bead stretches, then goes.
      b.neck = Math.max(0, (b.mass - 0.72) / 0.28);

      if (b.mass >= 1) {
        drops.push({
          x: b.x + b.vx,
          y: beadY + b.r * 1.5,
          r: b.r * 0.82,
          vy: 0.6,
          vx: b.vx * 0.3,
          top: beadY
        });
        b.mass = rnd() * 0.25;
        b.neck = 0;
      }
    }

    drawLedge();
    // Beads are painted over the ledge so the join reads as one fluid body.
    for (var j = 0; j < beads.length; j++) drawBead(beads[j]);

    // Falling drops.
    for (var k = drops.length - 1; k >= 0; k--) {
      var p = drops[k];
      p.vy += 0.42 * CFG.gravity * dt;
      p.y += p.vy * dt;
      // SMEAR: scroll velocity drags the fluid as it falls.
      p.x += (p.vx + velocity * 0.02 * CFG.smear) * dt;
      p.vx *= 0.98;

      drawDrop(p);

      if (p.y - p.top > 40 && streaks.length < 90) {
        streaks.push({ x: p.x, top: p.top, y: p.y, vy: 0.25, w: p.r * 0.5, life: 1 });
        p.top = p.y;
      }
      if (p.y > H + 60) drops.splice(k, 1);
    }

    requestAnimationFrame(tick);
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(resize, 180);
  }, { passive: true });

  resize();
  requestAnimationFrame(tick);
})();